import React, { useRef } from 'react';
import AsyncSelect from 'react-select/async';
import type { SingleValue } from 'react-select';

import { smallSelectStyle } from 'styles/components/reactSelect.styles';

import { deriveSirenFromFrVat } from 'utils/vat';
import { splitCompanyAddress } from 'utils/company';
import { useSearchCompaniesByNameLazyQuery, SearchCompaniesByNameQuery } from 'data/__generated__';

type CompanySuggestion = SearchCompaniesByNameQuery['searchCompaniesByName'][number];

type CompanyOption = {
    value: string;
    label: string;
    company: CompanySuggestion;
};

// Champs de facturation dérivés d'une entreprise Sirene sélectionnée. L'appelant décide comment les
// injecter dans son propre état (react-hook-form côté panier, state contrôlé côté Réglages).
export type SelectedCompany = {
    legalName: string;
    vat: string | null;
    siret: string;
    address: string;
    zipcode: string;
    city: string;
};

type CompanySearchSelectProps = {
    onSelect: (company: SelectedCompany) => void;
    placeholder?: string;
    dataTest?: string;
};

// Recherche Sirene débouncée par nom / SIRET / n° de TVA (FR → SIREN dérivé). À la sélection, renvoie
// via `onSelect` les champs de facturation dérivés. Composant partagé entre le formulaire de facturation
// du panier (checkout) et la page Réglages / Paiement, pour garder une seule implémentation de l'appel
// Sirene et du découpage d'adresse.
export const CompanySearchSelect: React.FC<CompanySearchSelectProps> = ({
    onSelect,
    placeholder = 'Rechercher par nom, SIRET ou n° de TVA…',
    dataTest = 'payment-company-search',
}) => {
    const [searchCompanies] = useSearchCompaniesByNameLazyQuery();
    const debounceRef = useRef<ReturnType<typeof setTimeout>>();

    // Recherche débouncée. On accepte un n° de TVA FR (on en dérive le SIREN) ou un nom / SIRET.
    function loadCompanyOptions(input: string): Promise<CompanyOption[]> {
        return new Promise((resolve) => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
            const term = (deriveSirenFromFrVat(input) ?? input).trim();
            if (term.length < 3) {
                resolve([]);
                return;
            }
            debounceRef.current = setTimeout(async () => {
                const { data } = await searchCompanies({ variables: { name: term, limit: 10 } });
                resolve(
                    (data?.searchCompaniesByName ?? []).map((company) => ({
                        value: company.siret,
                        label: `${company.legalName}${company.address ? ` — ${company.address}` : ''}${
                            company.isActive ? '' : ' — (fermée)'
                        }`,
                        company,
                    })),
                );
            }, 300);
        });
    }

    function handleChange(option: SingleValue<CompanyOption>) {
        const company = option?.company;
        if (!company) return;
        const { address, zipcode, city } = splitCompanyAddress(company.address);
        onSelect({
            legalName: company.legalName,
            vat: company.vat ?? null,
            siret: company.siret,
            address,
            zipcode,
            city,
        });
    }

    return (
        <div data-test={dataTest}>
            <AsyncSelect<CompanyOption, false>
                styles={smallSelectStyle}
                classNamePrefix="billing-company"
                placeholder={placeholder}
                cacheOptions
                value={null}
                loadOptions={loadCompanyOptions}
                onChange={handleChange}
                loadingMessage={() => 'Recherche…'}
                noOptionsMessage={({ inputValue }) =>
                    inputValue.trim().length < 3 ? 'Saisissez au moins 3 caractères' : 'Aucune entreprise trouvée'
                }
            />
        </div>
    );
};
